import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  CheckIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { AXIOS_INSTANCE } from '../../services/api-client';
import { cn } from '../../shared/lib/cn';
import { getTenantDisplayNames } from '../../shared/lib/tenant-display-name';
import { useAuth, type TenantMembership, type User } from '../../stores/useAuth';
import { ElizaLogo } from './ElizaLogoMark';

interface OrganizationSwitcherProps {
  collapsed?: boolean;
}

interface MenuPosition {
  left: number;
  top: number;
}

function getCurrentTenant(user: User | null): TenantMembership {
  const fallback: TenantMembership = {
    customer_id: user?.customer_id || 'default',
    customer_name: user?.customer_name || 'Organization',
    is_default: true,
    first_login_completed: true,
    created_at: '',
  };

  if (!user?.tenant_memberships?.length) {
    return fallback;
  }

  return user.tenant_memberships.find(m => m.customer_id === user.customer_id) || fallback;
}

export function OrganizationSwitcher({ collapsed = false }: OrganizationSwitcherProps) {
  const user = useAuth(s => s.user);
  const setUser = useAuth(s => s.setUser);
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [settingDefault, setSettingDefault] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<MenuPosition>({ left: 12, top: 56 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const currentTenant = useMemo(() => getCurrentTenant(user), [user]);
  const tenants = useMemo<TenantMembership[]>(() => {
    if (user?.tenant_memberships?.length) return user.tenant_memberships;
    return [currentTenant];
  }, [currentTenant, user?.tenant_memberships]);
  const tenantDisplayNames = useMemo(() => getTenantDisplayNames(tenants), [tenants]);
  const currentTenantDisplayName =
    tenantDisplayNames.get(currentTenant.customer_id) ?? currentTenant.customer_name;

  const updateMenuPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const panelWidth = 320;
    const gutter = 8;
    const preferredLeft = collapsed ? rect.right + gutter : rect.left;
    const preferredTop = collapsed ? rect.top : rect.bottom + gutter;

    setMenuPosition({
      left: Math.max(gutter, Math.min(preferredLeft, window.innerWidth - panelWidth - gutter)),
      top: Math.max(gutter, preferredTop),
    });
  }, [collapsed]);

  useLayoutEffect(() => {
    if (!open) return;
    updateMenuPosition();
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const handleViewportChange = () => updateMenuPosition();

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [open, updateMenuPosition]);

  const handleTenantChange = async (tenant: TenantMembership) => {
    if (tenant.customer_id === user?.customer_id || tenant.first_login_completed === false || switching) return;

    setSwitching(true);
    try {
      const response = await AXIOS_INSTANCE.post('/v1/auth/switch-tenant', {
        customer_id: tenant.customer_id,
      });

      const { access_token, refresh_token, user: newUserProfile } = response.data;
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      localStorage.removeItem('access_token');
      setUser(newUserProfile);
      window.location.reload();
    } catch (err) {
      console.error('Failed to switch tenant:', err);
    } finally {
      setSwitching(false);
    }
  };

  const handleSetDefault = async (tenant: TenantMembership, event: React.MouseEvent) => {
    event.stopPropagation();
    if (tenant.first_login_completed === false || settingDefault) return;

    setSettingDefault(tenant.customer_id);
    try {
      await AXIOS_INSTANCE.put('/v1/auth/default-tenant', {
        customer_id: tenant.customer_id,
      });

      if (user?.tenant_memberships) {
        setUser({
          ...user,
          tenant_memberships: user.tenant_memberships.map(m => ({
            ...m,
            is_default: m.customer_id === tenant.customer_id,
          })),
        });
      }
    } catch (err) {
      console.error('Failed to set default tenant:', err);
    } finally {
      setSettingDefault(null);
    }
  };

  if (!user) return null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={`Switch organization: ${currentTenantDisplayName}`}
        aria-expanded={open}
        aria-haspopup="menu"
        title={collapsed ? 'Switch organization' : undefined}
        onClick={() => setOpen(value => !value)}
        className={cn(
          'group flex min-w-0 items-center rounded-lg text-left transition-colors duration-150',
          'hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-eliza-red focus-visible:ring-offset-1',
          'dark:hover:bg-dark-surface-2 dark:focus-visible:ring-offset-dark-surface',
          collapsed ? 'h-10 w-10 justify-center' : 'h-12 w-full gap-1 px-2'
        )}
      >
        {collapsed ? (
          <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg text-charcoal dark:text-white">
            <ElizaLogo className="h-[15px] w-8" />
          </span>
        ) : (
          <>
            <span className="flex min-w-0 flex-1 items-center gap-0">
              <ElizaLogo className="h-[15px] w-[49px] flex-none -translate-y-0.5 text-charcoal dark:text-white" />
              <span className="-ml-0.5 mr-1 flex-none text-xs text-gray-400 dark:text-gray-500">
                for
              </span>
              <span className="min-w-0 truncate text-sm font-medium text-charcoal dark:text-white">
                {currentTenantDisplayName}
              </span>
            </span>
            <ChevronDownIcon
              className={cn(
                'h-4 w-4 flex-none text-gray-400 transition-transform',
                open && 'rotate-180'
              )}
            />
          </>
        )}
      </button>

      {open && (
        <div
          ref={menuRef}
          role="menu"
          aria-label="Switch organization"
          className="fixed z-[80] w-80 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl dark:border-dark-border dark:bg-dark-surface"
          style={{ left: menuPosition.left, top: menuPosition.top }}
        >
          <div className="border-b border-gray-200 px-4 py-3 dark:border-dark-border/30">
            <div className="truncate text-sm font-semibold text-charcoal dark:text-white">
              Switch organization
            </div>
          </div>

          <div className="max-h-72 overflow-y-auto p-2">
            {tenants.map(tenant => {
              const tenantDisplayName =
                tenantDisplayNames.get(tenant.customer_id) ?? tenant.customer_name;
              const isCurrent = tenant.customer_id === currentTenant.customer_id;
              const isDefault = tenant.is_default;
              const isUnavailable = tenant.first_login_completed === false;

              return (
                <div
                  key={tenant.customer_id}
                  className={cn(
                    'group flex items-center rounded-lg transition-colors',
                    isCurrent
                      ? 'bg-eliza-red/10 dark:bg-eliza-red/20'
                      : 'hover:bg-gray-100 dark:hover:bg-dark-surface-2'
                  )}
                >
                  <button
                    type="button"
                    role="menuitem"
                    disabled={isUnavailable || switching}
                    onClick={() => handleTenantChange(tenant)}
                    className={cn(
                      'flex min-w-0 flex-1 items-center gap-2 rounded-lg px-3 py-3 text-left',
                      'disabled:cursor-not-allowed disabled:opacity-70',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-eliza-red'
                    )}
                  >
                    <span className="min-w-0 flex-1">
                      <span
                        className={cn(
                          'block truncate text-sm font-medium',
                          isCurrent ? 'text-eliza-red' : 'text-charcoal dark:text-white'
                        )}
                      >
                        {tenantDisplayName}
                      </span>
                    </span>
                    {isUnavailable ? (
                      <ExclamationTriangleIcon className="h-4 w-4 flex-none text-amber-500" />
                    ) : isCurrent ? (
                      <CheckIcon className="h-4 w-4 flex-none text-eliza-red" />
                    ) : null}
                  </button>

                  {!isDefault && !isUnavailable && (
                    <button
                      type="button"
                      title={`Set ${tenantDisplayName} as default organization`}
                      aria-label={`Set ${tenantDisplayName} as default organization`}
                      onClick={(event) => handleSetDefault(tenant, event)}
                      className={cn(
                        'mr-2 rounded p-1 text-gray-400 opacity-0 transition-opacity',
                        'hover:text-amber-500 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-eliza-red',
                        'group-hover:opacity-100',
                        isCurrent && 'opacity-100',
                        settingDefault === tenant.customer_id && 'animate-pulse opacity-100'
                      )}
                    >
                      <StarIcon className="h-4 w-4" />
                    </button>
                  )}

                  {isDefault && (
                    <span className="mr-3 text-amber-500" title="Default organization">
                      <StarIconSolid className="h-4 w-4" />
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}

export default OrganizationSwitcher;
